import { NODE_TYPE_FIELD, NODE_TYPE_FINISH_PAGE, NODE_TYPE_ROW, NODE_TYPE_PAGE, NODE_TYPE_SECTION,
    NODE_TYPE_ABSTRACT, NODE_TYPE_CONFIGURATION, NODE_TYPE_GROUP } from './node.types.jsx';
import FieldMap from '../form.components/fields.map.jsx';
import { generateUID } from '../../common/utils.jsx';
import { EMPTY_STRING } from '../questionnaire.utils.jsx';

export class GenericDescription {
    constructor() {
        this.nodeType = NODE_TYPE_ABSTRACT;
        this.key = generateUID();
        this.props = {};
        this.childrenNodes = [];
        this.parent = null;
        this.bindPagesGroups = this.bindPagesGroups.bind(this);
        this.setProp = this.setProp.bind(this);
        this.getProp = this.getProp.bind(this);
        this.stringify = this.stringify.bind(this);
        this.include = this.include.bind(this);
        this.add = this.add.bind(this);
        this.recursiveFindDuplicatedFieldNames = this.recursiveFindDuplicatedFieldNames.bind(this);
        this.extractFields = this.extractFields.bind(this);
    }
    static isValidId(testString) {
        if ('string' === typeof testString) {
            return EMPTY_STRING !== testString.trim();
        }
        return false;
    }
    static isInvalidId(testString) { return !GenericDescription.isValidId(testString); }
    setProp(name, value) { this.props[name] = value; }
    getProp(name) { return this.props[name]; }
    include(childrenArray) {
        this.childrenNodes = childrenArray;
        this.childrenNodes.forEach((node) => { node.parent = this; });
        return this;
    }
    add(child) {
        this.childrenNodes.push(child);
        child.parent = this;
        return this;
    }
    simplify() {
        const clone = Object.assign({}, this);
        if (this.childrenNodes instanceof Array) {
            clone.childrenNodes = this.childrenNodes.map((children) => children.simplify());
        }
        delete clone.childrenNodes;
        return clone;
    }
    stringify() {
        const clone = this.simplify();
        this.validate(); //Will fall here if configuration contains duplicated names. It is not a bug!
        return JSON.stringify(clone);
    }
    recursiveFindDuplicatedFieldNames(calledFrom = EMPTY_STRING, fieldsMap = { }) {
        const nodeId = this.id || this.name || '--NO_ID--'; //name is used for configuration
        const nodePath = EMPTY_STRING === calledFrom ? nodeId : [ calledFrom, nodeId ].join('->');
        if (NODE_TYPE_FIELD === this.nodeType) { return fieldsMap; } //Can't validate just field
        if (undefined === this.childrenNodes) {
            console.warn(`${nodePath} childrenNodes property is undefined`);
            return fieldsMap;
        }
        this.childrenNodes.forEach((nodeItem) => {
            if (NODE_TYPE_FIELD === nodeItem.nodeType) {
                const root = (nodeItem.props && nodeItem.props.root) || false;
                const nodeName = root ? `root(${root}):(${nodeItem.name})` : nodeItem.name;
                const pathToField = `${nodeName}: ${nodePath}->${nodeName}`;
                if (fieldsMap[nodeName]) {
                    fieldsMap[nodeName].push(pathToField);
                } else {
                    fieldsMap[nodeName] = [pathToField];
                }
            } else if (NODE_TYPE_GROUP !== nodeItem.nodeType) {
                nodeItem.recursiveFindDuplicatedFieldNames(nodePath, fieldsMap);
            }
        });
        return fieldsMap;
    }
    validate(ERR_ON_DUPLICATES = true) {
        const fieldsMap = this.recursiveFindDuplicatedFieldNames();
        let duplicates = [];
        for (const fieldName in fieldsMap) {
            if (!fieldsMap.hasOwnProperty(fieldName)) { continue; }
            const fieldPaths = fieldsMap[fieldName];
            if (1 < fieldPaths.length) { duplicates = duplicates.concat(fieldPaths); }
        }
        if (0 !== duplicates.length) {
            console.log(duplicates);
            if (ERR_ON_DUPLICATES) {
                throw new Error('Questionnaire configuration contains duplicated names');
            }
        }
        return this;
    }
    bindPagesGroups() {
        const headerNames = GenericDescription.headerNames;
        this.childrenNodes.forEach((node, pageIndex, childrenNodes) => {
            if (-1 !== headerNames.indexOf(node.id)) {
                node.group = node.props.title;
                node.header = true;
            } else if ('NODE_TYPE_GROUP' !== node.nodeType) {
                let siblingIndex = pageIndex;
                while (siblingIndex--) {
                    const sibling = childrenNodes[pageIndex - siblingIndex];
                    if ('NODE_TYPE_GROUP' === sibling.nodeType) { node.group = sibling.group; }
                }
            }
        });
    }
    extractFields(fields, fieldsMap = []) {
        this.childrenNodes.forEach((nodeItem) => {
            if (NODE_TYPE_PAGE === nodeItem.nodeType && nodeItem.props.condition && !nodeItem.props.condition(fields)) {
                return fieldsMap;
            }
            if (NODE_TYPE_FIELD === nodeItem.nodeType) {
                const root = (nodeItem.props && nodeItem.props.root) || false;
                const nodeName = root ? `${root} ${nodeItem.name}` : nodeItem.name;
                if (fieldsMap[nodeName]) {
                    fieldsMap[nodeName].push(nodeName);
                } else {
                    fieldsMap.push(nodeName);
                }
            } else if (NODE_TYPE_GROUP !== nodeItem.nodeType) {
                nodeItem.extractFields(fields, fieldsMap);
            }
        });
        return fieldsMap;
    }
}

GenericDescription.childrenNamesMap = {
    NODE_TYPE_PAGE: 'rows',
    NODE_TYPE_ROW: 'fields',
    NODE_TYPE_CONFIGURATION: 'pages'
};

GenericDescription.headerNames = [
    'welcome-page',
    'personal-information',
    'comments',
    'final-step',
    'finish-page'
];

export class Field extends GenericDescription {
    constructor(name, inputType, props = {}) {
        super();
        this.nodeType = NODE_TYPE_FIELD;
        if (GenericDescription.isInvalidId(name)) { throw new Error(`Field() error: invalid field name: ${name}`); }
        if (undefined === FieldMap[inputType]) { throw new TypeError(`Field() error: unknown field type: ${inputType}`); }
        this.name = name;
        this.inputType = inputType;
        this.props = Object.assign({}, props);
    }
}


export class Row extends GenericDescription {
    constructor(id, props = {}) {
        super();
        this.nodeType = NODE_TYPE_ROW;
        if (GenericDescription.isInvalidId(id)) { throw new Error(`Row() error: invalid field name: ${id}`); }
        this.id = id;
        this.props = Object.assign({}, props);
    }
}


export class Section extends GenericDescription {
    constructor(id, props = {}) {
        super();
        this.nodeType = NODE_TYPE_SECTION;
        if (GenericDescription.isInvalidId(id)) { throw new Error(`Section() error: invalid field name: ${id}`); }
        this.id = id;
        this.props = Object.assign({}, props);
    }
}

export class Page extends GenericDescription {
    constructor(id, props = {}) {
        super();
        this.nodeType = NODE_TYPE_PAGE;
        if (GenericDescription.isInvalidId(id)) { throw new Error(`Page() error: invalid field name: ${id}`); }
        this.id = id;
        this.props = Object.assign({}, props);
    }
}

export class Configuration extends GenericDescription {
    constructor(configurationName, description) {
        super();
        this.nodeType = NODE_TYPE_CONFIGURATION;
        this.name = configurationName || '--Configuration name--';
        this.description = description || '--Description--';
    }
    include(childrenArray) {
        this.childrenNodes = childrenArray;
        this.childrenNodes.forEach((node) => { node.parent = this; });
        this.bindPagesGroups();
        return this;
    }
    add(child) {
        this.childrenNodes.push(child);
        child.parent = this;
        this.bindPagesGroups();
        return this;
    }
}

export class Group extends GenericDescription {
    constructor(id, group, props = {}) {
        super();
        this.nodeType = NODE_TYPE_GROUP;
        this.id = id;
        this.group = group;
        this.header = true;
        this.props = Object.assign({}, props);
    }
}

export class FinishPage extends GenericDescription {
    constructor(id, props = {}) {
        super();
        this.nodeType = NODE_TYPE_FINISH_PAGE;
        this.id = id;
        this.props = Object.assign({}, props);
    }
}
